import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, inject, DestroyRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ToastAlertService } from '@core/services';
import {
  CompanyModalComponent,
  EditLicensesComponent,
} from '../../../components/modals';
import { Business } from '../../../models/business.model';
import { BusinessService } from '../../../services/business.service';
import { ModalEmailComponent } from '@shared/components';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { NgIf, UpperCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SearchFieldComponent } from '@shared/components/search-field/search-field.component';
import { ToolbarComponent } from '@shared/components/toolbar/toolbar.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
@Component({
  selector: 'app-company-page',
  templateUrl: './company-page.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    ToolbarComponent,
    SearchFieldComponent,
    ButtonComponent,
    MatCardModule,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgIf,
    MatPaginatorModule,
    UpperCasePipe,
  ],
})
export class CompanyPageComponent {
  paginatorIntl: MatPaginatorIntl = new MatPaginatorIntl();
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  dataSource!: MatTableDataSource<Business>;
  fielInput!: string;
  private dialog = inject(MatDialog);
  private alertService = inject(ToastAlertService);
  private businessService = inject(BusinessService);
  private destroyRef = inject(DestroyRef);
  private _router = inject(Router);

  displayedColumns: string[] = [
    'name',
    'nit',
    'contractNumber',
    'invoiceNumber',
    'code',
    'address',
    'manager',
    'manager-contact',
    'usersCount',
    'usersCreated',
    'actions',
  ];
  constructor() {
    this.paginatorIntl = new MatPaginatorIntl();
    this.loadTable();
  }
  getRowStyles(element: number) {
    if (element === 2) {
      return { 'background-color': '#B0BEC5' }; // Gris para estado 1
    } else {
      return {}; // No se aplica ningún estilo adicional
    }
  }

  loadTable() {
    this.dataSource = new MatTableDataSource();
    this.businessService
      .getBusiness()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((request: any) => {
        if (request.code === 10000) {
          this.dataSource = new MatTableDataSource<Business>(request.reply);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.configureSortingAccessor();
          this.spanish();
        }
      });
  }

  configureSortingAccessor() {
    this.dataSource.sortingDataAccessor = (
      item: Business,
      property: string
    ): string | number => {
      if (property === 'usersCreated') {
        if (item.usersCount !== undefined && item.usersCreated !== undefined) {
          return item.usersCount - item.usersCreated;
        } else {
          return 0;
        }
      }
      const value = item[property as keyof Business];
      if (typeof value === 'string') {
        return value.toLowerCase();
      } else if (typeof value === 'number') {
        return value;
      }
      return '';
    };
  }

  modalAction(element?: string) {
    let width: string;
    if (window.innerWidth <= 768) {
      width = '90%';
    } else {
      width = '50%';
    }
    const dialogRef = this.dialog.open(CompanyModalComponent, {
      width: width,
      data: element,
      disableClose: true,
    });

    dialogRef
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.loadTable();
        }
      });
  }

  modalLicences(element?: string) {
    let width: string;
    if (window.innerWidth <= 768) {
      width = '90%';
    } else {
      width = '50%';
    }
    const dialogRef = this.dialog.open(EditLicensesComponent, {
      width: width,
      data: element,
      disableClose: true,
    });

    dialogRef
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.loadTable();
        }
      });
  }

  suspendCompany(data: Business) {
    const dialogEmail = this.dialog.open(ModalEmailComponent, {
      data: { name: data.name, template: 'Suspensión' },
      disableClose: true,
    });
    dialogEmail
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(request => {
        if (request) {
          this.businessService
            .sendEmail(data.idBusiness, 'SUSPENSION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((req: any) => {
              if (req.code === 10000) {
                this.suspend(data.idBusiness);
              } else {
                this.alertService.error(req.detail);
              }
            });
        }
      });
  }

  reactivateCompany(data: Business) {
    const dialogEmail = this.dialog.open(ModalEmailComponent, {
      data: { name: data.name, template: 'Reactivación' },
      disableClose: true,
    });
    dialogEmail
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(request => {
        if (request) {
          this.businessService
            .sendEmail(data.idBusiness, 'REACTIVACION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((req: any) => {
              if (req.code === 10000) {
                this.reactivate(data.idBusiness);
              } else {
                this.alertService.error(req.detail);
              }
            });
        }
      });
  }

  arrearsCompany(data: Business) {
    const dialogEmail = this.dialog.open(ModalEmailComponent, {
      data: { name: data.name, template: 'Mora' },
      disableClose: true,
    });
    dialogEmail
      .beforeClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(request => {
        if (request) {
          this.businessService
            .sendEmail(data.idBusiness, 'MORA')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: ({ code, detail }) => {
                if (code === 10000) {
                  this.alertService.sucess(
                    'La empresa ha sido notificada exitosamente'
                  );
                } else {
                  this.alertService.error(detail);
                }
              },
              error: () => {
                this.alertService.error('No se pudo notificar a la empresa');
              },
            });
        }
      });
  }

  private suspend(id: number | undefined) {
    this.businessService
      .suspend(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ code, detail }) => {
          if (code === 10000) {
            this.loadTable();
            this.alertService.sucess(
              'La empresa ha sido suspendida exitosamente'
            );
          } else {
            this.alertService.error(detail);
          }
        },
        error: () => {
          this.alertService.error('La empresa no se pudo suspender');
        },
      });
  }

  private reactivate(id: number | undefined) {
    this.businessService
      .reactivate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ code, detail }) => {
          if (code === 10000) {
            this.loadTable();
            this.alertService.sucess(
              'La empresa ha sido reactivada exitosamente'
            );
          } else {
            this.alertService.error(detail);
          }
        },
        error: () => {
          this.alertService.error('La empresa no se pudo suspender');
        },
      });
  }

  viewBuildingsByCompany($el: any) {
    this.businessService.setBusiness($el.idBusiness);
    this._router.navigate(['/admin/general/buildings', $el.idBusiness]);
  }

  spanish() {
    if (this.paginator) {
      this.paginator._intl.itemsPerPageLabel = 'Registros por página';
      this.paginator._intl.nextPageLabel = 'Página siguiente ';
      this.paginator._intl.previousPageLabel = 'Página anterior';
      this.paginator._intl.firstPageLabel = 'Primera página';
      this.paginator._intl.lastPageLabel = 'Última página';
      // Configuración del texto de la última página
      this.paginator._intl.getRangeLabel = (
        page: number,
        pageSize: number,
        length: number
      ) => {
        if (length === 0 || pageSize === 0) {
          return `0 de ${length}`;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        const endIndex =
          startIndex < length
            ? Math.min(startIndex + pageSize, length)
            : startIndex + pageSize;
        return `${startIndex + 1} - ${endIndex} de ${length}`;
      };
    }
  }
}
